import type { Unit, Company, Group } from '@/types';
import type { StationMetricsTableData } from '@/types/reports';
import { getUnits as getUnitsService, getUnitById as getUnitByIdService, updateHvacSettings } from '@/services/units';
import { supabase, currentSupabaseEnv } from './supabase';
import { staleWhileRevalidate, cacheKeys } from './cache';

/**
 * Opt-in hook for the stale-while-revalidate read paths.
 *
 * These fetchers hand back cached data first. `onRefresh` fires when the
 * background revalidation lands, so a caller can re-render with the truth on the
 * SAME visit rather than waiting for the next page load.
 */
export interface CachedFetchOptions<T> {
  onRefresh?: (data: T) => void;
}

interface UnitLocation {
  id: string;
  lat: number;
  lng: number;
  updated_at: string;
}

interface LocationsResponse {
  locations: UnitLocation[];
  timestamp: string;
}

interface UnitStatus {
  id: string;
  status: 'online' | 'offline' | 'idle' | 'unknown';
  soc?: number; // State of charge (battery %)
  lastUpdate: string;
  warning_count?: number;
  fault_count?: number;
}

interface StatusesResponse {
  statuses: UnitStatus[];
  timestamp: string;
}

/**
 * Get the full API URL for a given path
 * Uses relative paths in both development and production
 * - Development: Vite dev server proxy handles routing to Express backend
 * - Production: Vercel rewrites handle routing to serverless functions (same domain)
 *
 * @param path - API path (e.g., '/api/v1/units')
 * @returns Full URL ready for fetch
 */
function getApiUrl(path: string): string {
  // Use relative paths in both dev and production
  // Dev: Vite proxy routes /api/* to Express server on localhost:3001
  // Production: Vercel rewrites route /api/* to serverless functions
  return path;
}

/**
 * Get the current user's JWT token for authenticated API requests
 * Includes a 5-second timeout to prevent indefinite hangs from Supabase session retrieval
 */
async function getAuthToken(): Promise<string | null> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Session check timed out - please refresh the page')), 5000);
  });

  const sessionPromise = supabase.auth.getSession().then(({ data }) =>
    data.session?.access_token ?? null
  );

  return Promise.race([sessionPromise, timeoutPromise]);
}

/**
 * Make an authenticated API request with JWT token
 * Automatically includes Authorization header with current user's token
 * Handles both development (relative URLs via proxy) and production (absolute URLs)
 *
 * Always uses real Supabase JWT - backend validates against correct instance based on hostname
 *
 * @param path - API path (e.g., '/api/v1/units')
 * @param options - Standard fetch options
 * @param signal - Optional AbortSignal for request cancellation
 */
export async function authenticatedFetch(
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<Response> {
  const headers = new Headers(options.headers);

  // Always use real Supabase JWT token
  // Backend uses hostname-based routing to validate against correct Supabase instance
  const token = await getAuthToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('X-Supabase-Environment', currentSupabaseEnv);

  // Convert path to full URL (handles dev vs prod)
  const url = getApiUrl(path);

  return fetch(url, {
    ...options,
    headers,
    signal: signal ?? options.signal,
  });
}

/**
 * Fetch all units from Supabase
 */
export async function fetchUnits(): Promise<Unit[]> {
  return getUnitsService();
}

/**
 * Fetch lightweight location data for all units
 * Optimized for frequent polling with minimal data transfer
 *
 * @param since - Optional ISO timestamp to get only changes since that time
 */
export async function fetchUnitLocations(since?: string): Promise<LocationsResponse> {
  const params = new URLSearchParams();
  if (since) {
    params.set('since', since);
  }
  const url = `/api/v1/units/locations${params.toString() ? '?' + params.toString() : ''}`;

  const response = await authenticatedFetch(url);
  if (!response.ok) {
    // Try to read response body for error context
    let errorBody: string;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const json = await response.json();
        errorBody = JSON.stringify(json);
      } else {
        errorBody = await response.text();
      }
    } catch {
      errorBody = 'Unable to read response body';
    }

    throw new Error(
      `Failed to fetch unit locations: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
    );
  }
  return response.json();
}

/**
 * Fetch lightweight status data for all units
 * Optimized for frequent polling (every 15 seconds) with minimal data transfer
 *
 * @param since - Optional ISO timestamp to get only changes since that time
 */
export async function fetchUnitStatuses(since?: string): Promise<StatusesResponse> {
  const params = new URLSearchParams();
  if (since) {
    params.set('since', since);
  }
  const url = `/api/v1/units/statuses${params.toString() ? '?' + params.toString() : ''}`;

  const response = await authenticatedFetch(url);
  if (!response.ok) {
    // Try to read response body for error context
    let errorBody: string;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const json = await response.json();
        errorBody = JSON.stringify(json);
      } else {
        errorBody = await response.text();
      }
    } catch {
      errorBody = 'Unable to read response body';
    }

    throw new Error(
      `Failed to fetch unit statuses: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
    );
  }
  return response.json();
}

/**
 * Fetch a single unit by ID from Supabase
 */
export async function fetchUnitById(id: string): Promise<Unit> {
  const unit = await getUnitByIdService(id);
  if (!unit) {
    throw new Error('Unit not found');
  }
  return unit;
}

/**
 * Update HVAC settings via Supabase
 */
export async function updateHVAC(
  unitId: string,
  settings: {
    targetTemp?: number;
    mode?: 'cool' | 'heat' | 'fan' | 'off';
    fanSpeed?: 'low' | 'med' | 'high';
  }
): Promise<void> {
  // Note: fanSpeed not yet implemented in schema
  await updateHvacSettings(unitId, {
    targetTemp: settings.targetTemp,
    mode: settings.mode,
  });
}

/**
 * Fetch all companies
 * Requires authentication
 *
 * Uses stale-while-revalidate caching:
 * - Returns cached data immediately if available
 * - Fetches fresh data in background
 * - Works offline with cached data
 */
export async function getCompanies(
  options?: CachedFetchOptions<Company[]>
): Promise<Company[]> {
  const token = await getAuthToken();
  if (!token) throw new Error('User not authenticated');

  // Get user ID from token for cache key
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || 'anonymous';

  const cacheKey = cacheKeys.companies(userId);

  return staleWhileRevalidate(
    cacheKey,
    async () => {
      const response = await authenticatedFetch('/api/v1/companies');
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      return response.json();
    },
    'companies',
    undefined,
    options?.onRefresh
  );
}

/**
 * Fetch groups for a specific company
 * Requires authentication
 *
 * Uses stale-while-revalidate caching:
 * - Returns cached data immediately if available
 * - Fetches fresh data in background
 * - Works offline with cached data
 */
export async function getGroupsForCompany(companyId: string, companyName?: string): Promise<Group[]> {
  // Defensive: Pre-validate authentication before cache/fetch
  const token = await getAuthToken();
  if (!token) throw new Error('User not authenticated');

  const cacheKey = cacheKeys.groups(companyId);

  return staleWhileRevalidate(
    cacheKey,
    async () => {
      const response = await authenticatedFetch(`/api/v1/companies/${companyId}/groups`);
      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }
      return response.json();
    },
    'groups',
    companyName // Pass company name for readable cache logs
  );
}

/**
 * Fetch groups for multiple companies in a single bulk request
 * More efficient than calling getGroupsForCompany for each company (avoids N+1 pattern)
 *
 * Uses stale-while-revalidate caching:
 * - Cache key includes sorted companyIds for consistent hits
 * - Returns cached data immediately if available
 * - Fetches fresh data in background
 *
 * @param companyIds - Array of company IDs to fetch groups for
 * @returns Array of all groups across the requested companies
 */
export async function getBulkGroupsForCompanies(
  companyIds: string[],
  options?: CachedFetchOptions<Group[]>
): Promise<Group[]> {
  // Defensive: Pre-validate authentication before cache/fetch
  const token = await getAuthToken();
  if (!token) throw new Error('User not authenticated');

  // Get user ID for cache key (RBAC isolation)
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || 'anonymous';

  // Sort IDs for consistent cache keys regardless of selection order
  const sortedIds = [...companyIds].sort();
  const cacheKey = cacheKeys.groupsBulk(userId, sortedIds);

  return staleWhileRevalidate(
    cacheKey,
    async () => {
      const response = await authenticatedFetch('/api/v1/companies/groups/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyIds: sortedIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      return data.groups;
    },
    'groups',
    `bulk (${companyIds.length} companies)`,
    options?.onRefresh
  );
}

/**
 * Fetch units with optional company/group filters (multi-select)
 *
 * Uses stale-while-revalidate caching:
 * - Returns cached data immediately if available
 * - Fetches fresh data in background
 * - Works offline with cached data
 * - Cache key includes company and group filters for proper invalidation
 */
export async function getUnits(
  filters?: { companyIds?: string[]; groupIds?: string[]; },
  options?: CachedFetchOptions<Unit[]>
): Promise<Unit[]> {
  // CRITICAL: Get current user ID for user-specific cache key
  // Without this, a superadmin's cached units could leak to restricted users (RBAC bypass)
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id || 'anonymous';

  // Generate cache key based on user and filters
  const companyIds = filters?.companyIds || [];
  const groupIds = filters?.groupIds || [];
  const cacheKey = cacheKeys.units(userId, companyIds, groupIds);

  return staleWhileRevalidate(
    cacheKey,
    async () => {
      // If no filters provided, fall back to service layer for backward compatibility
      if (!filters?.companyIds?.length && !filters?.groupIds?.length) {
        return getUnitsService();
      }

      // Build URL with filters
      const params = new URLSearchParams();
      if (filters.companyIds && filters.companyIds.length > 0) {
        params.set('companyIds', filters.companyIds.join(','));
      }
      if (filters.groupIds && filters.groupIds.length > 0) {
        params.set('groupIds', filters.groupIds.join(','));
      }

      const url = `/api/v1/units?${params}`;

      // First, try to get fresh statuses to merge immediately
      // This avoids the "flash of offline" issue where cached unit list has old statuses
      let statuses: UnitStatus[] = [];
      try {
        const statusParams = new URLSearchParams({ since: '' });
        const statusUrl = `/api/v1/units/statuses?${statusParams}`; // Get all latest statuses
        const statusResponse = await authenticatedFetch(statusUrl);
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          statuses = statusData.statuses || [];
        }
      } catch (e) {
        console.warn('[getUnits] Failed to pre-fetch statuses:', e);
      }

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch units');
      }

      const units = await response.json();

      // If we have fresh statuses, apply them before returning/caching
      if (statuses.length > 0) {
        const statusMap = new Map(statuses.map(s => [s.id, s.status]));
        return units.map((unit: Unit) => ({
          ...unit,
          status: statusMap.get(unit.id) || unit.status
        }));
      }

      return units;
    },
    'units',
    undefined,
    options?.onRefresh
  );
}

/**
 * Fetch station energy metrics for the Reports page
 * Queries real data from unit_energy_metrics table in Supabase
 *
 * @param groupIds - Array of group IDs to filter by
 * @param dateRange - Date range for aggregation
 * @param page - Current page number (1-indexed)
 * @param pageSize - Number of items per page
 * @param signal - Optional AbortSignal for request cancellation
 * @param modelCodes - Optional array of model codes to filter by (e.g., ['2RE', '1RE'])
 * @param industryTypes - Optional array of industry types to filter by (e.g., ['film', 'event'])
 * @param includeAllDeployments - For internal users, include data from all deployment periods
 */
export async function fetchStationMetrics(
  groupIds: string[],
  dateRange: { start: Date | null; end: Date | null },
  page: number = 1,
  pageSize: number = 25,
  signal?: AbortSignal,
  modelCodes?: string[],
  industryTypes?: string[],
  includeAllDeployments?: boolean,
  unitIds?: string[] | null
): Promise<StationMetricsTableData> {
  const params = new URLSearchParams();

  // __unassigned_<companyId>__ sentinels are sent as-is: the station-metrics
  // route splits them into p_unassigned_company_ids (PR #593). Stripping them
  // here excluded unassigned units — and an unassigned-only selection omitted
  // groupIds entirely, silently widening the query to fleet-wide.
  const selectedGroupIds = groupIds.filter(Boolean);

  if (selectedGroupIds.length > 0) {
    params.set('groupIds', selectedGroupIds.join(','));
  }

  if (dateRange.start && dateRange.end) {
    // Format as YYYY-MM-DD in local timezone to avoid off-by-one errors
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    params.set('startDate', formatLocalDate(dateRange.start));
    params.set('endDate', formatLocalDate(dateRange.end));
  }

  // Add model codes filter for filtering by unit model prefix (e.g., "2RE", "1RE")
  if (modelCodes && modelCodes.length > 0) {
    params.set('modelCodes', modelCodes.join(','));
  }

  // Add industry types filter for filtering by industry (e.g., "film", "event")
  // This ensures that when MED is selected under Film, only Film MED units are included
  if (industryTypes && industryTypes.length > 0) {
    params.set('industryTypes', industryTypes.join(','));
  }

  // Include all deployment periods (internal users only)
  // When true, shows data from all deployment periods with visual annotations
  if (includeAllDeployments) {
    params.set('includeAllDeployments', 'true');
  }

  // Reports → Unit Status click-to-focus: only these units (server caps at 500)
  if (unitIds && unitIds.length > 0) {
    params.set('unitIds', unitIds.join(','));
  }

  params.set('page', page.toString());
  params.set('pageSize', pageSize.toString());

  const url = `/api/v1/energy/station-metrics?${params}`;

  const response = await authenticatedFetch(url, {}, signal);
  if (!response.ok) {
    let errorBody: string;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const json = await response.json();
        errorBody = JSON.stringify(json);
      } else {
        errorBody = await response.text();
      }
    } catch {
      errorBody = 'Unable to read response body';
    }

    throw new Error(
      `Failed to fetch station metrics: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`
    );
  }

  return response.json();
}
